import { Work as WorkIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  Card,
  Button
} from '@mui/joy';
import useUser from './auth/useUser';
import LargeLoader from './LargeLoader';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { RECORDING_STATES } from '../constants';
import { useFeedback } from './feedback/Service';
import { parseGraphQLError } from '../utils';
import { gql, useMutation } from '@apollo/client';
import useUpToDateRecordingState from './useUpToDateRecordingState';
import { Video } from './VideoJs';

const DELETE_INTERVIEW = gql`
  mutation DeleteInterview($id: String!) {
    deleteInterview(id: $id)
  }
`;

const DELETE_APPLICATION = gql`
  mutation DeleteApplication($id: String!) {
    deleteApplication(id: $id)
  }
`;

const APPLICATION_STEPS = {
  QUESTIONNAIRE: 'Questionnaire',
  // INITIAL_SCREENING: 'Initial Screening',
  VIDEO_INTERVIEW: 'Video Interview',
  AWAITING_DECISION: 'Awaiting Decision',
  REJECTED: 'Not Selected',
}

const APPLICATION_STEPS_COLORS = {
  [APPLICATION_STEPS.QUESTIONNAIRE]: 'primary',
  [APPLICATION_STEPS.VIDEO_INTERVIEW]: 'primary',
  [APPLICATION_STEPS.AWAITING_DECISION]: 'primary',
  [APPLICATION_STEPS.REJECTED]: 'danger',
}

const APPLICATION_WORKFLOW = [
  { name: APPLICATION_STEPS.QUESTIONNAIRE, description: 'Complete the questionnaire', url: 'questionnaire' },
  { name: APPLICATION_STEPS.VIDEO_INTERVIEW, description: 'Complete the video interview', url: 'video-interview-intro' },
  { name: APPLICATION_STEPS.AWAITING_DECISION, description: 'Awaiting decision', url: null },
  { name: APPLICATION_STEPS.REJECTED, description: 'Not selected', url: null },
]

const hasScreeningQuestions = (questions) => {
  if (!questions) return false;
  try {
    return JSON.parse(questions).length > 0;
  } catch {
    return false;
  }
};

const getApplicationStep = (application) => {
  if (application.isRejected) {
    return APPLICATION_STEPS.REJECTED;
  }
  const hasQuestions = hasScreeningQuestions(application.job?.questions);
  if (hasQuestions && !application.questionnaire) {
    return APPLICATION_STEPS.QUESTIONNAIRE;
  }
  if (hasQuestions && !application.questionnaire.passed) {
    return APPLICATION_STEPS.AWAITING_DECISION;
  }
  if (!application.interview || !application.interview?.isFinished) {
    return APPLICATION_STEPS.VIDEO_INTERVIEW;
  }
  return APPLICATION_STEPS.AWAITING_DECISION;
}

const getApplicationStepDetails = (application) => {
  const step = getApplicationStep(application);
  const stepDetails = APPLICATION_WORKFLOW.find(s => s.name === step);
  return stepDetails || {};
}

const ApplicationItem = ({ application, isAdmin, isDemo }) => {
  const feedback = useFeedback();
  const [deleteInterview, { loading: deletingInterview }] = useMutation(DELETE_INTERVIEW);
  const [deleteApplication, { loading: deletingApplication }] = useMutation(DELETE_APPLICATION);
  const step = getApplicationStepDetails(application);
  const recordingState = useUpToDateRecordingState(application.interview?.recording || {}).state;
  const { refetch, loading: refetching } = useUser({ notifyOnNetworkStatusChange: true });
  const navigate = useNavigate();

  const handleShowVideoInterview = () => {
    feedback.alert({
      component: <Video recordingId={application.interview.recording._id} durations={application.interview.durations} />,
      confirmButtonText: "Close",
      maxWidth: "md",
      fullWidth: true,
      clickAwayClose: true,
      removePadding: true,
    });
  }

  const handleDeleteInterview = async () => {
    if (!await feedback.confirm({ title: "Delete Interview?" }))
      return;
    if (!application.interview?._id)
      return console.error('No interview ID found for deletion');
    try {
      await deleteInterview({ variables: { id: application.interview._id } });
      await refetch({ fetchPolicy: 'network-only' });
      feedback.snackbar({ text: 'Interview deleted successfully', type: 'success' });
    } catch (error) {
      console.error('Error deleting interview:', error);
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    }
  }

  const handleDeleteApplication = async () => {
    if (!await feedback.confirm({ title: "Delete Application?" }))
      return;
    if (!application._id)
      return console.error('No application ID found for deletion');
    try {
      await deleteApplication({ variables: { id: application._id } });
      await refetch({ fetchPolicy: 'network-only' });
      feedback.snackbar({ text: 'Application deleted successfully', type: 'success' });
      navigate(`/company/${application.client._id}/job/${application.job._id}/intro`);
    } catch (error) {
      console.error('Error deleting application:', error);
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    }
  }

  return (
    <ListItem
      key={application._id}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'neutral.outlinedBorder',
        borderRadius: 'sm',
        p: 2,
      }}
    >
      <ListItemDecorator>
        <WorkIcon />
      </ListItemDecorator>

      <ListItemContent>
        <Typography level="title-lg">{application.job.title}</Typography>
        {!application.job.hideClientName &&
          <Typography level="body-sm">{application.client.company}</Typography>
        }
        <Chip
          size="sm"
          variant={APPLICATION_STEPS_COLORS[step.name] !== 'danger' ? 'soft' : 'solid'}
          color={APPLICATION_STEPS_COLORS[step.name] || 'primary'}
          sx={{ mt: 1, px: 2, py: 0.5 }}
        >
          {step.name}
        </Chip>
      </ListItemContent>
      {step.url &&
        <Button
          variant="outlined"
          size="sm"
          component={Link}
          to={`/company/${application.client._id}/job/${application.job._id}/${step.url}`}
        >
          Continue
        </Button>
      }
      {recordingState === RECORDING_STATES.READY &&
        <Button
          variant="soft"
          size="sm"
          onClick={handleShowVideoInterview}
        >
          Show Video Interview
        </Button>
      }
      {(application.interview?._id && isAdmin) &&
        <Button
          variant="outlined"
          size="sm"
          color="danger"
          loading={deletingInterview || refetching}
          onClick={handleDeleteInterview}
        >
          Delete Interview
        </Button>
      }
      {isDemo &&
        <Button
          variant="outlined"
          size="sm"
          color="danger"
          loading={deletingApplication || refetching}
          onClick={handleDeleteApplication}
        >
          Delete Demo Application
        </Button>
      }
    </ListItem>
  );
}

const Applications = () => {
  const { loading, error, user } = useUser();

  const applications = useMemo(() => user?.talent?.applications || [], [user]);

  if (loading) return <LargeLoader />;

  if (error) return (
    <Box sx={{ p: 4 }}>
      <Typography level="h4" color="danger">
        Error loading jobs
      </Typography>
      <Typography color="danger">{error.message}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Typography level="h2" sx={{ mb: 3 }}>
        Your {user?.client ? "Demo" : "Job"} Applications
      </Typography>

      {applications.length > 0 ? (
        <List>
          {applications.map((application) => (
            <ApplicationItem key={application._id} application={application} isAdmin={user.role === "ADMIN"} isDemo={application.client?._id === user?.client?._id} />
          ))}
        </List>
      ) : (
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <Typography>
            You have not applied for any jobs yet.
          </Typography>
        </Card>
      )}
    </Box >
  );
};

export default Applications;
